import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './lib/store'

// Layouts
import Layout from './components/layout/Layout'
import AdminLayout from './components/admin/AdminLayout'

// Public Pages
import BlogHomePage from './pages/public/BlogHomePage'
import HomePage from './pages/public/HomePage'
import VehiclesPage from './pages/public/VehiclesPage'
import VehicleDetailPage from './pages/public/VehicleDetailPage'
import ToursPage from './pages/public/ToursPage'
import TourDetailPage from './pages/public/TourDetailPage'
import NewsPage from './pages/public/NewsPage'
import NewsDetailPage from './pages/public/NewsDetailPage'
import ShortsPage from './pages/public/ShortsPage'
import CheckoutPage from './pages/public/CheckoutPage'
import StaticPage from './pages/public/StaticPage'
import ContactPage from './pages/public/ContactPage'
import SearchResultsPage from './pages/public/SearchResultsPage'
import NotFoundPage from './pages/public/NotFoundPage'
import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'

// Customer Pages
import CustomerDashboard from './pages/customer/DashboardPage'
import BookingsPage from './pages/customer/BookingsPage'
import WalletPage from './pages/customer/WalletPage'

// Owner Pages
import OwnerDashboard from './pages/owner/OwnerDashboard'
import MyVehiclesPage from './pages/owner/MyVehiclesPage'
import WithdrawalPage from './pages/owner/WithdrawalPage'

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard'
import WithdrawalManagement from './pages/admin/WithdrawalManagement'
import NewsManagement from './pages/admin/NewsManagement'
import NewsEditor from './pages/admin/NewsEditor'
import UserManagement from './pages/admin/UserManagement'
import VehicleManagement from './pages/admin/VehicleManagement'
import VehicleEditor from './pages/admin/VehicleEditor'
import BookingManagement from './pages/admin/BookingManagement'
import TourManagement from './pages/admin/TourManagement'
import TourEditor from './pages/admin/TourEditor'
import PagesManagement from './pages/admin/PagesManagement'
import CategoriesManagement from './pages/admin/CategoriesManagement'
import PageBuilder from './pages/admin/PageBuilder'
import ArticleLayoutBuilder from './pages/admin/ArticleLayoutBuilder'
import SettingsPage from './pages/admin/SettingsPage'
import CommentsManagement from './pages/admin/CommentsManagement'
import ReviewsManagement from './pages/admin/ReviewsManagement'
import VideosManagement from './pages/admin/VideosManagement'

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }: { children: React.ReactNode; allowedRoles: string[] }) {
    const { user, isAuthenticated } = useAuthStore()

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />
    }

    if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role || '')) {
        return <Navigate to="/" replace />
    }

    return <>{children}</>
}

function App() {
    return (
        <BrowserRouter>
            <Routes>
                {/* Shorts - Fullscreen without header/footer */}
                <Route path="/shorts" element={<ShortsPage />} />
                <Route path="/shorts/:videoId" element={<ShortsPage />} />

                {/* Public Routes with Layout (Sidebar) */}
                <Route path="/" element={<Layout />}>
                    <Route index element={<BlogHomePage />} />
                    <Route path="rental" element={<HomePage />} />
                    <Route path="vehicles" element={<VehiclesPage />} />
                    <Route path="vehicles/:id" element={<VehicleDetailPage />} />
                    <Route path="tours" element={<ToursPage />} />
                    <Route path="tours/:id" element={<TourDetailPage />} />
                    <Route path="blog" element={<NewsPage />} />
                    <Route path="blog/:category" element={<NewsPage />} />
                    <Route path="blog/:category/:slug" element={<NewsDetailPage />} />
                    <Route path="news" element={<NewsPage />} />
                    <Route path="news/:slug" element={<NewsDetailPage />} />
                    <Route path="checkout" element={<CheckoutPage />} />

                    {/* Static Pages */}
                    <Route path="about" element={<StaticPage />} />
                    <Route path="contact" element={<ContactPage />} />
                    <Route path="terms" element={<StaticPage />} />
                    <Route path="privacy" element={<StaticPage />} />
                    <Route path="faq" element={<StaticPage />} />

                    <Route path="login" element={<LoginPage />} />
                    <Route path="register" element={<RegisterPage />} />

                    {/* Search Results */}
                    <Route path="search/:query" element={<SearchResultsPage />} />

                    {/* Customer Routes */}
                    <Route path="dashboard" element={
                        <ProtectedRoute allowedRoles={['customer']}>
                            <CustomerDashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="dashboard/bookings" element={
                        <ProtectedRoute allowedRoles={['customer']}>
                            <BookingsPage />
                        </ProtectedRoute>
                    } />
                    <Route path="dashboard/bookings/:id" element={
                        <ProtectedRoute allowedRoles={['customer']}>
                            <BookingsPage />
                        </ProtectedRoute>
                    } />
                    <Route path="dashboard/wallet" element={
                        <ProtectedRoute allowedRoles={['customer']}>
                            <WalletPage />
                        </ProtectedRoute>
                    } />

                    {/* Owner Routes (inside Layout for now) */}
                    <Route path="owner" element={
                        <ProtectedRoute allowedRoles={['owner']}>
                            <OwnerDashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="owner/vehicles" element={
                        <ProtectedRoute allowedRoles={['owner']}>
                            <MyVehiclesPage />
                        </ProtectedRoute>
                    } />
                    <Route path="owner/vehicles/add" element={
                        <ProtectedRoute allowedRoles={['owner']}>
                            <MyVehiclesPage />
                        </ProtectedRoute>
                    } />
                    <Route path="owner/vehicles/:id/edit" element={
                        <ProtectedRoute allowedRoles={['owner']}>
                            <MyVehiclesPage />
                        </ProtectedRoute>
                    } />
                    <Route path="owner/bookings" element={
                        <ProtectedRoute allowedRoles={['owner']}>
                            <OwnerDashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="owner/earnings" element={
                        <ProtectedRoute allowedRoles={['owner']}>
                            <OwnerDashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="owner/earnings/withdraw" element={
                        <ProtectedRoute allowedRoles={['owner']}>
                            <WithdrawalPage />
                        </ProtectedRoute>
                    } />
                </Route>

                {/* Admin Routes with AdminLayout */}
                <Route path="/admin" element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <AdminLayout />
                    </ProtectedRoute>
                }>
                    <Route index element={<AdminDashboard />} />
                    <Route path="users" element={<UserManagement />} />
                    <Route path="vehicles" element={<VehicleManagement />} />
                    <Route path="vehicles/new" element={<VehicleEditor />} />
                    <Route path="vehicles/:id/edit" element={<VehicleEditor />} />
                    <Route path="tours" element={<TourManagement />} />
                    <Route path="tours/new" element={<TourEditor />} />
                    <Route path="tours/:id/edit" element={<TourEditor />} />
                    <Route path="bookings" element={<BookingManagement />} />
                    <Route path="withdrawals" element={<WithdrawalManagement />} />
                    <Route path="blog" element={<NewsManagement />} />
                    <Route path="blog/new" element={<NewsEditor />} />
                    <Route path="blog/:id/edit" element={<NewsEditor />} />
                    <Route path="categories" element={<CategoriesManagement />} />
                    <Route path="page-builder" element={<PageBuilder />} />
                    <Route path="article-layout" element={<ArticleLayoutBuilder />} />
                    <Route path="pages" element={<PagesManagement />} />
                    <Route path="videos" element={<VideosManagement />} />
                    <Route path="comments" element={<CommentsManagement />} />
                    <Route path="reviews" element={<ReviewsManagement />} />
                    <Route path="analytics" element={<AdminDashboard />} />
                    <Route path="settings" element={<SettingsPage />} />
                </Route>

                {/* Shorts - Full Screen (No Layout) */}
                <Route path="shorts" element={<ShortsPage />} />

                {/* 404 Not Found - Wrapped in Layout manually */}
                <Route path="*" element={<NotFoundPage />} />
            </Routes>
        </BrowserRouter>
    )
}

export default App
